import { collection, getDocs, orderBy, query, where } from "firebase/firestore";
import { distanceBetween, geohashQueryBounds } from "geofire-common";
import { useEffect, useMemo, useState } from "react";
import { firestore } from "../../firebase";
import { PlaceDocument } from "../../types/Place";

export function useBoundedPlaceQuery(
  mapBounds: google.maps.LatLngBounds | null,
  radiusInKm: number = 10
) {
  const [places, setPlaces] = useState<PlaceDocument[]>([]);

  useEffect(() => {
    const fetchPlaces = async () => {
      if (!mapBounds) {
        return;
      }

      const placesCollection = collection(firestore, "places");

      // Get center point of bounds for radius-based query
      const center = mapBounds.getCenter();
      const centerLat = center.lat();
      const centerLng = center.lng();

      // Generate geohash query bounds
      const bounds = geohashQueryBounds(
        [centerLat, centerLng],
        radiusInKm * 1000
      );

      // Execute multiple queries (geohash ranges)
      const promises = bounds.map((b) => {
        return getDocs(
          query(
            placesCollection,
            orderBy("geohash"),
            where("geohash", ">=", b[0]),
            where("geohash", "<=", b[1])
          )
        );
      });

      const snapshots = await Promise.all(promises);

      // Combine results from all queries
      const allDocs = snapshots.reduce((acc, snapshot) => {
        snapshot.docs.forEach((doc) => {
          // Avoid duplicates
          if (!acc.some((existingDoc) => existingDoc.id === doc.id)) {
            acc.push(doc);
          }
        });
        return acc;
      }, [] as any[]);

      // Filter by actual distance and map bounds
      const placeDocuments = allDocs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as PlaceDocument[];

      const filteredPlaces = placeDocuments.filter((place) => {
        const lat = place.location?.latitude ?? 0;
        const lng = place.location?.longitude ?? 0;

        // Check if within actual distance
        const distanceInKm = distanceBetween(
          [centerLat, centerLng],
          [lat, lng]
        );
        const withinDistance = distanceInKm <= radiusInKm;

        // Also check if within map bounds for rectangular queries
        const ne = mapBounds.getNorthEast();
        const sw = mapBounds.getSouthWest();
        const withinBounds =
          lat >= sw.lat() &&
          lat <= ne.lat() &&
          lng >= sw.lng() &&
          lng <= ne.lng();

        return withinDistance && withinBounds;
      });

      setPlaces(filteredPlaces);
    };

    fetchPlaces();
  }, [mapBounds, radiusInKm]);

  return useMemo(() => {
    return { places };
  }, [places]);
}
